// ** MUI Import
import Box from "@mui/material/Box";
import { useTheme } from "@mui/material/styles";
import Typography from "@mui/material/Typography";

// ** Util Import
import { hexToRGBA } from "src/@core/utils/hex-to-rgba";

// SalesPark v3 brand accent. Kept as a literal so the loader stays brand-consistent
// in both light and dark, independent of the MUI palette.
const ACCENT = "#666cff";

/******************************************************************
 * ##: FallbackSpinner
 * Full-screen branded loading overlay: a soft pulsing brand glow, a
 * comet-tail arc spinning on a faint track, and an optional title with
 * animated trailing dots. Respects prefers-reduced-motion.
 * @param {String} [title] - optional loading message shown under the ring
 * @returns {JSX.Element} - the loading overlay
 * History:
 * 05-07-2026: Redesigned to the v3 brand line (comet ring + glow + animated dots)
 ******************************************************************/
const FallbackSpinner = ({ title }) => {
  // ** Hook
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";

  // Strip a trailing ellipsis so the animated dots are the single source of motion.
  const label = typeof title === "string" ? title.replace(/[.…]+\s*$/, "").trim() : title;

  const trackColor = isDark ? "rgba(234,234,255,0.14)" : "rgba(102,108,255,0.16)";

  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        flexDirection: "column",
        justifyContent: "center",
        position: "fixed",
        width: "100%",
        height: "100%",
        top: 0,
        left: 0,
        gap: "22px",
        px: 3,
        textAlign: "center",
        backgroundColor: hexToRGBA(theme.palette.background.default, 0.92),
        backdropFilter: "blur(2px)",
        WebkitBackdropFilter: "blur(2px)",
        zIndex: 9999,
      }}
    >
      {/* Ring stack: ambient glow, faint full track, and a spinning comet arc */}
      <Box sx={{ position: "relative", width: 76, height: 76, display: "grid", placeItems: "center" }}>
        {/* Comet-tail arc: a masked conic gradient rotating over the track */}
        <Box
          sx={{
            position: "absolute",
            width: 76,
            height: 76,
            borderRadius: "50%",
            background: `conic-gradient(from 90deg, transparent 6%, ${ACCENT})`,
            WebkitMask: "radial-gradient(farthest-side, transparent calc(100% - 5px), #000 calc(100% - 5px))",
            mask: "radial-gradient(farthest-side, transparent calc(100% - 5px), #000 calc(100% - 5px))",
            animation: "spinnerSpin 0.9s linear infinite",
            "@keyframes spinnerSpin": {
              to: { transform: "rotate(360deg)" },
            },
            "@media (prefers-reduced-motion: reduce)": { animation: "spinnerSpin 1.8s linear infinite" },
          }}
        />
      </Box>

      {label !== undefined && label !== "" && (
        <Typography
          sx={{
            fontWeight: 500,
            fontSize: "0.98rem",
            letterSpacing: "0.01em",
            color: theme.palette.text.secondary,
            display: "inline-flex",
            alignItems: "baseline",
            maxWidth: "min(560px, 90vw)",
            "& .spinner-dots > span": {
              animation: "spinnerDot 1.4s ease-in-out infinite",
            },
            "& .spinner-dots > span:nth-of-type(2)": { animationDelay: "0.18s" },
            "& .spinner-dots > span:nth-of-type(3)": { animationDelay: "0.36s" },
            "@keyframes spinnerDot": {
              "0%, 80%, 100%": { opacity: 0.22 },
              "40%": { opacity: 1 },
            },
            "@media (prefers-reduced-motion: reduce)": {
              "& .spinner-dots > span": { animation: "none", opacity: 0.6 },
            },
          }}
        >
          {label}
          <Box component="span" className="spinner-dots" aria-hidden="true" sx={{ ml: "2px" }}>
            <span>.</span>
            <span>.</span>
            <span>.</span>
          </Box>
        </Typography>
      )}
    </Box>
  );
};

export default FallbackSpinner;
